import { OverlayContainer } from '@angular/cdk/overlay';
import {
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ScrollTopModule } from 'primeng/scrolltop';
import { filter } from 'rxjs';
import { PlatformZones } from '../../enums/client-admin-zone';
import { CurrentUser } from '../../models/current-user';
import { LanguageCode } from '../../models/language-code';
import { ConstantsService } from '../../services/constants.service';
import {
    DARK_MODE_CLASS,
    DarkModeService,
} from '../../services/dark-mode.service';
import { DatePickerIntlService } from '../../services/date-picker-intl.service';
import { MainLayoutComponent } from '../main-layout/main-layout.component';
import { TopNavbarComponent } from '../top-navbar/top-navbar.component';

@Component({
    selector: 'apcam-skeleton',
    templateUrl: './skeleton.component.html',
    styleUrls: ['./skeleton.component.scss'],
    imports: [
        MainLayoutComponent,
        MatButtonModule,
        MatDividerModule,
        MatIconModule,
        MatSidenavModule,
        RouterOutlet,
        ScrollTopModule,
        TopNavbarComponent,
        TranslatePipe,
    ],
    host: {
        '[class.darkMode]': 'darkMode()',
        '(window:resize)': 'sizeChange($event)',
    },
})
export class SkeletonComponent implements OnInit {
    /**
     * Current user
     */
    @Input()
    currentUser: CurrentUser = new CurrentUser('', '');

    /**
     * Input property specifying the client or admin zone for the component.
     *
     * @usageNotes
     * The `zone` property is used to specify the client or admin zone within the component.
     * It is of type PlatformZones, with a default value of `PlatformZones.ADMIN_ZONE`.
     *
     * @default PlatformZones.ADMIN_ZONE
     */
    @Input()
    zone: PlatformZones = PlatformZones.ADMIN_ZONE;

    @Input({ required: true })
    public availableLanguages!: WritableSignal<LanguageCode[]>;

    @Input({ required: true })
    notificationCount: WritableSignal<number> = signal(0);

    @Output()
    openNotificationDialog = new EventEmitter();

    protected windowWidth!: number;

    protected SIDENAV_WIDTH: number = ConstantsService.SIDENAV_WIDTH;
    protected SIDENAV_BREAKPOINT: number = ConstantsService.SIDENAV_BREAKPOINT;
    protected TOOLBAR_BREAKPOINT: number = ConstantsService.TOOLBAR_BREAKPOINT;

    protected TOOLBAR_STANDARD_HEIGHT: number =
        ConstantsService.TOOLBAR_STANDARD_HEIGHT;
    protected TOOLBAR_MOBILE_HEIGHT: number =
        ConstantsService.TOOLBAR_MOBILE_HEIGHT;

    protected idMain: string = ConstantsService.MAIN_ID;

    /**
     * Signal representing the dark mode state.
     * Used to dynamically bind the 'darkMode' class to the host element.
     */
    protected darkMode!: WritableSignal<boolean>;

    protected readonly PlatformZones = PlatformZones;
    protected readonly DARK_MODE_CLASS = DARK_MODE_CLASS;

    constructor(
        private readonly darkModeService: DarkModeService,
        private readonly datePickerIntlService: DatePickerIntlService,
        private readonly destroyRef: DestroyRef,
        private readonly overlay: OverlayContainer,
        private readonly router: Router
    ) {
        // Initialize signal in constructor where darkModeService is available
        this.darkMode = signal(this.darkModeService.getDarkMode());
    }

    ngOnInit(): void {
        this.windowWidth = window.innerWidth;

        // Date adapter for date picker
        this.datePickerIntlService.monitorLocale();

        // Change focus upon Navigation end
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.focusMain();
                },
            });
    }

    protected focusMain(): void {
        const main = document.querySelector('#' + this.idMain) as HTMLElement;
        if (main) {
            main.tabIndex = 0;
            main.focus();
            main.tabIndex = -1;
        }
    }

    protected getDarkMode(): boolean {
        return this.darkMode();
    }

    protected sizeChange(event: Event): void {
        const target = event.target as Window;
        if (target) {
            this.windowWidth = target.innerWidth;
        }
    }

    protected toggleDarkMode(): void {
        const darkModeClassStr = DARK_MODE_CLASS;
        this.darkModeService.toggleDarkMode();
        this.darkMode.set(this.darkModeService.getDarkMode());

        // Update overlay container classes (CDK overlay needs manual DOM manipulation)
        if (this.darkMode()) {
            this.overlay.getContainerElement().classList.add(darkModeClassStr);
        } else {
            this.overlay
                .getContainerElement()
                .classList.remove(darkModeClassStr);
        }
    }
}
